import { useMemo } from 'react';

import usePageContext from '@rover/react-lib/src/context/usePageContext';
import type { ExtractEffectReturnType } from '@rover/react-lib/src/lib/redux-duck/types';
import {
  effects as marketingOptInEffects,
  selectors as marketingOptInSelectors,
} from '@rover/react-lib/src/redux/ducks/marketingOptIn.duck';
import { useApiMarketingOptInCreate } from '@rover/rsdk/src/apiClient/latest/api-marketing-opt-in-create';
import { useApiMarketingOptInRetrieve } from '@rover/rsdk/src/apiClient/latest/api-marketing-opt-in-retrieve';
import type { MarketingOptInModalRequest } from '@rover/rsdk/src/apiClient/latest/model/marketingOptInModalRequest';
import type { MarketingOptInIntent } from '@rover/types/src/MarketingOptIn';

import useDucks from './useDucks';

export type MarketingOptInSelectorProps = {
  shouldShowMarketingOptInModal: boolean;
  variation?: string;
  petType?: string | null;
  channels?: string[];
};

const selector = (state): MarketingOptInSelectorProps => ({
  shouldShowMarketingOptInModal: marketingOptInSelectors.getShouldShowMarketingOptInModal(state),
  variation: marketingOptInSelectors.getVariation(state),
  petType: marketingOptInSelectors.getPetType(state),
  channels: marketingOptInSelectors.getChannels(state),
});

const effects = {
  submitMarketingOptInIntent: marketingOptInEffects.submitMarketingOptInIntent,
  fetchShouldShowMarketingOptInModal: marketingOptInEffects.fetchShouldShowMarketingOptInModal,
};

export type MarketingOptInEffectsProps = {
  submitMarketingOptInIntent: (
    value: MarketingOptInIntent
  ) => ExtractEffectReturnType<(typeof effects)['submitMarketingOptInIntent']>;
  fetchShouldShowMarketingOptInModal: ExtractEffectReturnType<
    (typeof effects)['fetchShouldShowMarketingOptInModal']
  >;
};

const useMarketingOptInShim = (): MarketingOptInSelectorProps &
  Omit<MarketingOptInEffectsProps, 'fetchShouldShowMarketingOptInModal'> => {
  const { isReactQueryRoute } = usePageContext();

  const { data: reactQueryData } = useApiMarketingOptInRetrieve({
    query: { enabled: isReactQueryRoute },
  });

  const { data: duckData } = useDucks({ selector, effects });

  const { mutate } = useApiMarketingOptInCreate();

  const returnObject = useMemo(
    () => ({
      shouldShowMarketingOptInModal: reactQueryData?.showModal ?? false,
      variation: reactQueryData?.variation,
      petType: reactQueryData?.petType,
      channels: reactQueryData?.channels,
      submitMarketingOptInIntent: (data: MarketingOptInModalRequest) => mutate({ data }),
    }),
    [mutate, reactQueryData]
  );

  return isReactQueryRoute ? returnObject : duckData;
};

export default useMarketingOptInShim;
